import { defineStore } from "pinia";
import { ref, Ref } from "vue";
import {
  IInspection,
  IInspectionFilter,
  IManualInspection,
} from "@/features/inspections/inspection.types";
import { inspectionService } from "@/features/inspections/inspection.service";
import { useNotification } from "@/utils/notification";
import { ITableHeader } from "@/types/table";
import { archiveService } from "@/features/archive/archive.service";

const { pushNotification } = useNotification();

export const useInspectionStore = defineStore("inspection", () => {
  const commonFilter = ref<IInspectionFilter>({
    company_id: "",
    driver_id: "",
    team_id: "",
    report_uploaded: null,
    search: "",
    per_page: 50,
    page: 1,
  });

  const inspectionsHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      type: "centert",
      width: "3%",
    },
    {
      name: "company_name",
      label: "Company",
      width: "20%",
    },
    {
      name: "driver_name",
      label: "Driver",
      width: "20%",
    },
    {
      name: "period",
      label: "Period",
      width: "10%",
    },
    {
      name: "processed_time",
      label: "Processed Time",
      width: "8%",
    },
    {
      name: "last_driver_task_user_name",
      label: "Last Task By",
      width: "14%",
    },
    {
      name: "last_driver_task_completed_time",
      label: "Last Task At",
      width: "9%",
    },
    {
      name: "has_eld_history_changes_after_last_task",
      label: "Other edits",
      width: "8%",
    },
    {
      name: "type",
      label: "Source",
      width: "8%",
    },
    {
      name: "actions",
      label: "Actions",
      width: "4%",
    },
  ];

  async function getInspections(loading: Ref<boolean>): Promise<IInspection> {
    try {
      loading.value = true;
      const { data } = await inspectionService.getInspections({
        ...commonFilter.value,
      });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {
        items: [],
        meta: {
          page: 1,
          per_page: 50,
          total: 0,
        },
      };
    } finally {
      loading.value = false;
    }
  }

  async function uploadReport(
    loading: Ref<boolean>,
    inspectionId: string,
    file: File
  ) {
    try {
      loading.value = true;
      const { data } = await inspectionService.uploadReport(inspectionId, file);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
    } finally {
      loading.value = false;
    }
  }

  async function downloadReport(loading: Ref<boolean>, inspectionId: string) {
    try {
      loading.value = true;
      const { data } = await inspectionService.downloadReport(inspectionId);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
    } finally {
      loading.value = false;
    }
  }

  async function deleteReport(loading: Ref<boolean>, inspectionId: string) {
    try {
      loading.value = true;
      const { data } = await inspectionService.deleteReport(inspectionId);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
    } finally {
      loading.value = false;
    }
  }

  async function updateComment(
    loading: Ref<boolean>,
    inspectionId: string,
    payload: any
  ) {
    try {
      loading.value = true;
      const { data } = await inspectionService.updateComment(
        inspectionId,
        payload
      );
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
    } finally {
      loading.value = false;
    }
  }

  async function createManualInspection(
    loading: Ref<boolean>,
    payload: IManualInspection,
    cl: any
  ) {
    loading.value = true;
    await inspectionService
      .createManualInspection(payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    commonFilter,
    inspectionsHeaders,
    getInspections,
    uploadReport,
    downloadReport,
    deleteReport,
    updateComment,
    createManualInspection,
  };
});
